import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Phone, Mail, FileText, CheckCircle2,
  Camera, Upload, Loader2, MapPin, Briefcase, Calendar, Hash,
  AlertCircle
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import apiClient from '@/services/apiClient';

interface DocumentFile {
  id: string;
  url: string;
  file_name: string;
  mime_type: string;
}

interface BusinessDetails {
  id: string;
  name: string;
  category: string;
  description: string;
  years_in_operation: number;
  location: { address: string; latitude: number; longitude: number };
  contact: { primary_phone: string; dial_code: string; country_code: string; whatsapp: string; email: string };
  registration: { national_id: string; kra_pin: string; trade_license_number: string; business_registration_number: string };
  logo_url: string | null;
  documents: {
    kra_pin_certificate: DocumentFile[];
    registration_certificate: DocumentFile[];
    premises_photos: DocumentFile[];
    owner_photo: DocumentFile | null;
  };
}

const ReadOnlyField = ({ label, value, icon: Icon, isMissing = false }: { label: string; value?: string; icon: any; isMissing?: boolean }) => (
  <div className="flex flex-col mb-4">
    <label className="text-xs font-semibold text-light-subtle dark:text-gray-400 mb-1 flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" /> {label}
      {isMissing && <span className="w-1.5 h-1.5 rounded-full bg-danger ml-1" title="Missing data"></span>}
    </label>
    <div className="bg-gray-50 dark:bg-[#2A2A2A] rounded-xl px-4 py-3 text-sm text-light-text dark:text-gray-100 font-medium border border-transparent">
      {value || <span className="text-gray-400 dark:text-gray-500 italic">Not provided</span>}
    </div>
  </div>
);

const BusinessDetailsPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const toastCtx = useToast();

  const [details, setDetails] = useState<BusinessDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoUploading, setLogoUploading] = useState(false);
  const [premisesUploading, setPremisesUploading] = useState(false);

  useEffect(() => {
    if (businessId) fetchDetails();
  }, [businessId]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/businesses/${businessId}/details`);
      if (res.data.success) {
        setDetails(res.data.data);
      }
    } catch (err: any) {
      toastCtx.error('Error', err.response?.data?.message || 'Failed to load business details');
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (file: File, type: 'logo' | 'premises') => {
    try {
      if (type === 'logo') setLogoUploading(true);
      else setPremisesUploading(true);

      const formData = new FormData();
      formData.append('type', type);
      formData.append('photo', file);

      const res = await apiClient.put(`/businesses/${businessId}/update-photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        toastCtx.success('Photo updated successfully');
        fetchDetails(); // Reload to get fresh URLs
      }
    } catch (err: any) {
      toastCtx.error('Upload Failed', err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setLogoUploading(false);
      setPremisesUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#121212] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!details) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#121212] flex flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-danger mb-4" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Business not found</h2>
        <button onClick={() => navigate('/business')} className="mt-4 text-primary hover:underline">Return to Dashboard</button>
      </div>
    );
  }

  const cardClass = "rounded-2xl p-6 bg-white dark:bg-[#1E1E1E] shadow-sm";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#121212] pb-12">
      {/* Custom Header */}
      <div className="sticky top-0 z-40 h-16 bg-white dark:bg-[#1E1E1E] shadow-sm flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-[#2A2A2A] hover:opacity-80 transition"
          >
            <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-gray-200" />
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">Your Business Profile</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="space-y-6">

          {/* Verification Badge */}
          <div className="bg-success/10 border border-success/20 rounded-2xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center text-success">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-success dark:text-green-400">Official Business Record</h3>
              <p className="text-xs text-success/80 dark:text-green-500/80 mt-0.5">
                These are your verified registration details. To update text fields, please contact support.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* General Info Column */}
            <div className="md:col-span-2 space-y-6">
              
              {/* About */}
              <div className={cardClass}>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 border-b border-gray-100 dark:border-gray-800 pb-2">Business Overview</h3>
                <ReadOnlyField label="Business Name" value={details.name} icon={Building2} />
                <ReadOnlyField label="Category" value={details.category} icon={Briefcase} />
                <ReadOnlyField label="Years in Operation" value={details.years_in_operation ? `${details.years_in_operation} years` : undefined} icon={Calendar} isMissing={!details.years_in_operation} />
                
                <div className="flex flex-col">
                  <label className="text-xs font-semibold text-light-subtle dark:text-gray-400 mb-1 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" /> Description
                    {!details.description && <span className="w-1.5 h-1.5 rounded-full bg-danger ml-1" title="Missing data"></span>}
                  </label>
                  <div className="bg-gray-50 dark:bg-[#2A2A2A] rounded-xl px-4 py-3 text-sm text-light-text dark:text-gray-100 font-medium">
                    {details.description || <span className="text-gray-400 dark:text-gray-500 italic">No description provided</span>}
                  </div>
                </div>
              </div>

              {/* Contact & Location */}
              <div className={cardClass}>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 border-b border-gray-100 dark:border-gray-800 pb-2">Contact & Location</h3>
                <ReadOnlyField label="Primary Phone" value={`${details.contact.dial_code} ${details.contact.primary_phone || ''}`} icon={Phone} isMissing={!details.contact.primary_phone} />
                <ReadOnlyField label="Email Address" value={details.contact.email} icon={Mail} isMissing={!details.contact.email} />
                <ReadOnlyField label="Physical Address" value={details.location.address} icon={MapPin} isMissing={!details.location.address} />
              </div>

              {/* Registration Compliance */}
              <div className={cardClass}>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 border-b border-gray-100 dark:border-gray-800 pb-2">Registration Compliance</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <ReadOnlyField label="KRA PIN" value={details.registration.kra_pin} icon={Hash} isMissing={!details.registration.kra_pin} />
                  <ReadOnlyField label="National ID" value={details.registration.national_id} icon={Hash} isMissing={!details.registration.national_id} />
                  <ReadOnlyField label="Business Reg. Number" value={details.registration.business_registration_number} icon={Hash} isMissing={!details.registration.business_registration_number} />
                  <ReadOnlyField label="Trade License Number" value={details.registration.trade_license_number} icon={Hash} isMissing={!details.registration.trade_license_number} />
                </div>
              </div>

              {/* Premises Photos */}
              <div className={cardClass}>
                <div className="flex items-center justify-between mb-4 border-b border-gray-100 dark:border-gray-800 pb-2">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Premises Photos</h3>
                  <label className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary text-xs font-bold rounded-lg cursor-pointer hover:bg-primary/20 transition">
                    {premisesUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    Add Photo
                    <input type="file" accept="image/jpeg,image/png,image/jpg,image/webp" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0], 'premises')} className="hidden" />
                  </label>
                </div>
                
                {details.documents.premises_photos.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                    No premises photos added. Showcase your physical location.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {details.documents.premises_photos.map((photo, i) => (
                      <a 
                        key={i} 
                        href={photo.url} 
                        target="_blank" 
                        rel="noreferrer"
                        className="relative group aspect-square rounded-xl overflow-hidden bg-gray-100 dark:bg-[#2A2A2A]"
                      >
                        <img src={photo.url} alt="Premises" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                      </a>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Logo Column */}
            <div className="md:col-span-1">
              <div className={cardClass + " flex flex-col items-center text-center"}>
                <label className="relative cursor-pointer group mb-4">
                  <div className="w-32 h-32 rounded-3xl bg-gray-100 dark:bg-[#2A2A2A] shadow-md border-2 border-white dark:border-[#333333] flex items-center justify-center overflow-hidden">
                    {logoUploading ? (
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    ) : details.logo_url ? (
                      <img src={details.logo_url} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-4xl font-black text-gray-300 dark:text-gray-600">
                        {details.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white">
                      <Camera className="h-6 w-6 mb-1" />
                      <span className="text-xs font-bold">Change</span>
                    </div>
                  </div>
                  <input type="file" accept="image/jpeg,image/png,image/jpg,image/webp" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0], 'logo')} className="hidden" />
                </label>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{details.name}</h2>
                <p className="text-sm text-primary font-semibold mt-1">{details.category}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetailsPage;
